/* ═══════════════════════════════════════════════════════════════
   ADAPTADOR LEGACY — adminSchedule
   Lee del motor canónico programsMaster.ts y persons.ts.
   Exporta en el formato antiguo esperado por /admin/schedule.
   ═══════════════════════════════════════════════════════════════ */

import { canonicalSlots, canonicalPrograms, getProgramById } from './programsMaster';
import { canonicalPersons } from './persons';
import type { CanonicalScheduleSlot } from './programsMaster';

/* ─── Tipos legacy (sin cambios) ─── */
export type ProgramType = 'musica' | 'programa_vivo' | 'predicacion' | 'entrevista' | 'noticias' | 'deportes' | 'especial' | 'evento';

export type ScheduleView = 'dia' | 'semana' | 'mes' | 'lista';

export interface RadioProgram {
  id: number;
  name: string;
  host: string;
  hostAvatar: string;
  description: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  type: ProgramType;
  isLive: boolean;
  isRecurring: boolean;
  isActive: boolean;
  cancelled?: boolean;
  image?: string;
  startDate?: string;
  color?: string;
  stationId?: number;
}

export const programTypeLabels: Record<ProgramType, string> = {
  musica: 'Música',
  programa_vivo: 'Programa en vivo',
  predicacion: 'Predicación',
  entrevista: 'Entrevista',
  noticias: 'Noticias',
  deportes: 'Deportes',
  especial: 'Especial',
  evento: 'Evento',
};

export const programTypeColors: Record<ProgramType, { bg: string; text: string; icon: string; stripe: string }> = {
  musica: { bg: 'bg-indigo-100', text: 'text-indigo-700', icon: 'ri-music-line', stripe: 'border-l-indigo-400' },
  programa_vivo: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: 'ri-broadcast-line', stripe: 'border-l-emerald-400' },
  predicacion: { bg: 'bg-violet-100', text: 'text-violet-700', icon: 'ri-mental-health-line', stripe: 'border-l-violet-400' },
  entrevista: { bg: 'bg-amber-100', text: 'text-amber-700', icon: 'ri-mic-line', stripe: 'border-l-amber-400' },
  noticias: { bg: 'bg-sky-100', text: 'text-sky-700', icon: 'ri-newspaper-line', stripe: 'border-l-sky-400' },
  deportes: { bg: 'bg-orange-100', text: 'text-orange-700', icon: 'ri-football-line', stripe: 'border-l-orange-400' },
  especial: { bg: 'bg-rose-100', text: 'text-rose-700', icon: 'ri-star-line', stripe: 'border-l-rose-400' },
  evento: { bg: 'bg-teal-100', text: 'text-teal-700', icon: 'ri-calendar-event-line', stripe: 'border-l-teal-400' },
};

export const dayNames: Record<number, string> = {
  0: 'Domingo', 1: 'Lunes', 2: 'Martes', 3: 'Miércoles', 4: 'Jueves', 5: 'Viernes', 6: 'Sábado',
};

export const dayShortNames: Record<number, string> = {
  0: 'DOM', 1: 'LUN', 2: 'MAR', 3: 'MIÉ', 4: 'JUE', 5: 'VIE', 6: 'SÁB',
};

/* ─── Adaptador: canonical slot → legacy RadioProgram ─── */
const slotToProgramMap: Record<string, string> = {
  'prog-001': 'El Mañanero',
  'prog-002': 'La Hora Pico',
  'prog-003': 'Zona Deportiva',
  'prog-004': 'Entrevistas Exclusivas',
  'prog-005': 'Beats & Bytes',
  'prog-006': 'Top 40 Countdown',
  'prog-007': 'Sesiones Acústicas',
  'prog-008': 'Noticias al Instante',
  'prog-009': 'Mañana Activa',
  'prog-010': 'Tardes de Pop',
  'prog-011': 'Noche de Estrenos',
  'prog-012': 'Viernes de Fiesta',
  'prog-013': 'Miércoles de Conciertos',
  'prog-014': 'Domingo Familiar',
};

function canonicalSlotToRadioProgram(slot: CanonicalScheduleSlot, idx: number): RadioProgram {
  const cp = slot.programId ? getProgramById(slot.programId) : null;
  const title = cp?.title || slot.specialEventName || 'Programa';
  const hostPersonId = cp?.hostPersonIds?.[0];
  const hostPerson = hostPersonId ? canonicalPersons.find((p) => p.id === hostPersonId) : null;
  const hostName = hostPerson?.displayName || 'Equipo';

  return {
    id: idx + 1,
    name: title,
    host: hostName,
    hostAvatar: hostPerson?.avatar || '',
    description: cp?.description || '',
    dayOfWeek: slot.dayOfWeek,
    startTime: slot.startTime,
    endTime: slot.endTime,
    type: slot.type as ProgramType,
    isLive: slot.isLive,
    isRecurring: slot.isRecurring,
    isActive: slot.isActive,
    cancelled: slot.cancelled,
    image: cp?.coverImage,
    startDate: slot.startDate,
    stationId: 1,
  };
}

/* ─── Construir el Schedule desde los slots canónicos ─── */
function buildAdminSchedule(): RadioProgram[] {
  return canonicalSlots
    .filter((s) => s.isActive)
    .map((s, idx) => canonicalSlotToRadioProgram(s, idx));
}

export const adminSchedule: RadioProgram[] = buildAdminSchedule();

/* ─── Helpers legacy ─── */
export function getStationSchedule(stationId: number): RadioProgram[] {
  return adminSchedule.filter((p) => p.stationId === stationId || !p.stationId);
}

export function getProgramsByDay(stationId: number, dayOfWeek: number): RadioProgram[] {
  return getStationSchedule(stationId)
    .filter((p) => p.dayOfWeek === dayOfWeek)
    .sort((a, b) => a.startTime.localeCompare(b.startTime));
}

export function getNowPlaying(stationId: number): { now: RadioProgram | null; next: RadioProgram | null } {
  const now = new Date();
  const currentDay = now.getDay();
  const currentTime = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0');

  const allPrograms = getStationSchedule(stationId).filter((p) => p.dayOfWeek === currentDay);
  const sorted = allPrograms.sort((a, b) => a.startTime.localeCompare(b.startTime));

  let nowPlaying: RadioProgram | null = null;
  let nextPlaying: RadioProgram | null = null;

  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].startTime <= currentTime && sorted[i].endTime > currentTime) {
      nowPlaying = sorted[i];
      nextPlaying = i + 1 < sorted.length ? sorted[i + 1] : null;
      break;
    }
  }

  if (!nowPlaying && sorted.length > 0) {
    const upcoming = sorted.filter((p) => p.startTime > currentTime);
    if (upcoming.length > 0) {
      nextPlaying = upcoming[0];
    }
  }

  return { now: nowPlaying, next: nextPlaying };
}